import { useState } from 'react';
import { FormField, FormStructure } from '../features/surveys/types';
import { validateSurveyField } from '../features/surveys/surveyFieldValidation';

interface Props {
  structure: FormStructure;
  onSubmit: (data: Record<string, any>) => Promise<void>;
  loading?: boolean;
}

/** Validación de un campo (reutilizable en formularios embebidos, p. ej. QMS). */
// eslint-disable-next-line react-refresh/only-export-components -- helper reutilizado fuera del componente
export function validatePublicSurveyField(field: FormField, value: unknown): string | null {
  return validateSurveyField(field, value);
}

function getInputClassName(hasError: boolean): string {
  const borderClass = hasError ? 'border-destructive' : 'border-input';
  return `w-full rounded-md border ${borderClass} bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none text-foreground`;
}

function FieldLabel({ label, required, className = 'mb-1' }: { label: string; required?: boolean; className?: string }) {
  return (
    <label className={`block text-sm font-medium text-foreground ${className}`}>
      {label}
      {required && <span className="text-destructive ml-1">*</span>}
    </label>
  );
}

function FieldError({ error }: { error?: string }) {
  if (!error) return null;
  return <p className="text-sm text-destructive mt-1">{error}</p>;
}

interface FieldRenderProps {
  field: FormField;
  value: any;
  error?: string;
  onChange: (fieldId: string, value: any) => void;
  onCheckboxMultipleChange: (fieldId: string, option: string, checked: boolean) => void;
  onBlur?: (fieldId: string) => void;
}

function TextInput({ field, value, error, onChange, onBlur }: FieldRenderProps) {
  return (
    <div key={field.id}>
      <FieldLabel label={field.label} required={field.required} />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(field.id, e.target.value)}
        onBlur={() => onBlur?.(field.id)}
        placeholder={field.placeholder}
        className={getInputClassName(!!error)}
      />
      <FieldError error={error} />
    </div>
  );
}

function TextareaInput({ field, value, error, onChange, onBlur }: FieldRenderProps) {
  return (
    <div key={field.id}>
      <FieldLabel label={field.label} required={field.required} />
      <textarea
        value={value}
        onChange={(e) => onChange(field.id, e.target.value)}
        onBlur={() => onBlur?.(field.id)}
        placeholder={field.placeholder}
        rows={4}
        className={getInputClassName(!!error)}
      />
      <FieldError error={error} />
    </div>
  );
}

function NumberInput({ field, value, error, onChange, onBlur }: FieldRenderProps) {
  return (
    <div key={field.id}>
      <FieldLabel label={field.label} required={field.required} />
      <input
        type="number"
        value={value}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === '') {
            onChange(field.id, '');
            return;
          }
          const parsed = Number(raw);
          onChange(field.id, Number.isNaN(parsed) ? raw : parsed);
        }}
        onBlur={() => onBlur?.(field.id)}
        placeholder={field.placeholder}
        min={field.validation?.min}
        max={field.validation?.max}
        className={getInputClassName(!!error)}
      />
      <FieldError error={error} />
    </div>
  );
}

function DateInput({ field, value, error, onChange, onBlur }: FieldRenderProps) {
  return (
    <div key={field.id}>
      <FieldLabel label={field.label} required={field.required} />
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(field.id, e.target.value)}
        onBlur={() => onBlur?.(field.id)}
        className={getInputClassName(!!error)}
      />
      <FieldError error={error} />
    </div>
  );
}

function SelectInput({ field, value, error, onChange, onBlur }: FieldRenderProps) {
  return (
    <div key={field.id}>
      <FieldLabel label={field.label} required={field.required} />
      <select
        value={value}
        onChange={(e) => onChange(field.id, e.target.value)}
        onBlur={() => onBlur?.(field.id)}
        className={getInputClassName(!!error)}
      >
        <option value="">Seleccione una opción</option>
        {field.options?.map((option) => (
          <option key={option} value={option}>{option}</option>
        ))}
      </select>
      <FieldError error={error} />
    </div>
  );
}

function RadioInput({ field, value, error, onChange, onBlur }: FieldRenderProps) {
  return (
    <div key={field.id}>
      <FieldLabel label={field.label} required={field.required} className="mb-2" />
      <div className="space-y-2">
        {field.options?.map((option) => (
          <label key={option} className="flex items-center gap-2">
            <input
              type="radio"
              name={field.id}
              value={option}
              checked={value === option}
              onChange={(e) => onChange(field.id, e.target.value)}
              onBlur={() => onBlur?.(field.id)}
              className="border-input"
            />
            <span className="text-sm text-foreground">{option}</span>
          </label>
        ))}
      </div>
      <FieldError error={error} />
    </div>
  );
}

function CheckboxMultipleInput({
  field,
  value,
  error,
  onCheckboxMultipleChange,
  onBlur,
}: FieldRenderProps) {
  const selected = (value as string[]) || [];
  return (
    <div key={field.id}>
      <FieldLabel label={field.label} required={field.required} className="mb-2" />
      <div className="space-y-2">
        {field.options?.map((option) => (
          <label key={option} className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={selected.includes(option)}
              onChange={(e) => onCheckboxMultipleChange(field.id, option, e.target.checked)}
              onBlur={() => onBlur?.(field.id)}
              className="rounded border-input"
            />
            <span className="text-sm text-foreground">{option}</span>
          </label>
        ))}
      </div>
      <FieldError error={error} />
    </div>
  );
}

function CheckboxInput({ field, value, error, onChange, onBlur }: FieldRenderProps) {
  return (
    <div key={field.id} className="flex items-center gap-2">
      <input
        type="checkbox"
        checked={value === true}
        onChange={(e) => onChange(field.id, e.target.checked)}
        onBlur={() => onBlur?.(field.id)}
        className="rounded border-input"
      />
      <label className="text-sm font-medium text-foreground">
        {field.label}
        {field.required && <span className="text-destructive ml-1">*</span>}
      </label>
      <FieldError error={error} />
    </div>
  );
}

const FIELD_RENDERERS: Record<string, React.FC<FieldRenderProps>> = {
  text: TextInput,
  textarea: TextareaInput,
  number: NumberInput,
  date: DateInput,
  select: SelectInput,
  radio: RadioInput,
  'checkbox-multiple': CheckboxMultipleInput,
  checkbox: CheckboxInput,
};

export interface DynamicSurveyFieldsProps {
  structure: FormStructure;
  formData: Record<string, unknown>;
  errors: Record<string, string>;
  onChange: (fieldId: string, value: any) => void;
  onCheckboxMultipleChange: (fieldId: string, option: string, checked: boolean) => void;
  /** Valida un campo al salir (blur); si no se pasa, solo valida al enviar. */
  onFieldBlur?: (fieldId: string) => void;
}

/** Campos dinámicos sin `<form>` envolvente (para combinar con otros campos en un solo envío). */
export function DynamicSurveyFields({
  structure,
  formData,
  errors,
  onChange,
  onCheckboxMultipleChange,
  onFieldBlur,
}: DynamicSurveyFieldsProps) {
  const renderField = (field: FormField) => {
    const Renderer = FIELD_RENDERERS[field.type];
    if (!Renderer) return null;
    return (
      <Renderer
        key={field.id}
        field={field}
        value={formData[field.id] ?? ''}
        error={errors[field.id]}
        onChange={onChange}
        onCheckboxMultipleChange={onCheckboxMultipleChange}
        onBlur={onFieldBlur}
      />
    );
  };

  return <div className="space-y-6">{structure.fields.map((field) => renderField(field))}</div>;
}

export function PublicSurveyForm({ structure, onSubmit, loading }: Props) {
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);

  const validateField = (fieldId: string, value: unknown) => {
    const field = structure.fields.find((f) => f.id === fieldId);
    if (!field) return;
    const message = validateSurveyField(field, value);
    setErrors((prev) => {
      const next = { ...prev };
      if (message) next[fieldId] = message;
      else delete next[fieldId];
      return next;
    });
  };

  const handleChange = (fieldId: string, value: any) => {
    setFormData((prev) => ({ ...prev, [fieldId]: value }));
    const field = structure.fields.find((f) => f.id === fieldId);
    if (!field) return;
    if (errors[fieldId] || field.type === 'number' || field.type === 'date') {
      validateField(fieldId, value);
    }
  };

  const handleFieldBlur = (fieldId: string) => {
    validateField(fieldId, formData[fieldId]);
  };

  const handleCheckboxMultipleChange = (fieldId: string, option: string, checked: boolean) => {
    const currentValue = formData[fieldId] as string[] || [];
    let newValue: string[];
    if (checked) {
      newValue = [...currentValue, option];
    } else {
      newValue = currentValue.filter((v) => v !== option);
    }
    handleChange(fieldId, newValue);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    // Validar todos los campos
    const newErrors: Record<string, string> = {};
    structure.fields.forEach((field) => {
      const error = validatePublicSurveyField(field, formData[field.id]);
      if (error) {
        newErrors[field.id] = error;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      await onSubmit(formData);
      setSubmitted(true);
    } catch (error: any) {
      setErrors({ submit: error.message || 'Error al enviar el formulario' });
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-8">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 text-green-600 mb-4">
          <svg
            className="w-8 h-8"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-foreground mb-2">¡Gracias!</h2>
        <p className="text-muted-foreground">
          Tu respuesta ha sido enviada exitosamente.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <DynamicSurveyFields
        structure={structure}
        formData={formData}
        errors={errors}
        onChange={handleChange}
        onCheckboxMultipleChange={handleCheckboxMultipleChange}
        onFieldBlur={handleFieldBlur}
      />
      
      {errors.submit && (
        <div className="p-3 bg-destructive/10 border border-destructive/20 rounded text-destructive">
          {errors.submit}
        </div>
      )}

      <button
        type="submit"
        disabled={loading}
        className="w-full btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? 'Enviando...' : 'Enviar Respuesta'}
      </button>
    </form>
  );
}






